import React, { useState, useRef, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { List } from "react-window";
import { useInfiniteLoader } from "react-window-infinite-loader";
import useMutation from "../../../hooks/useMutation";
import { ASSETS_BULK_REMOVE_MUTATION } from "../../../constants/graphql";

import AssetsWrap from "../../capture/AssetsWrap";
import { useInfinityScroll } from "../../../utils/infinityScroll";
import ConfirmDeleteModal from "../../../components/images/ConfirmDeleteModal";
import KitListItem from "./KitListItem";

const ITEM_HEIGHT = 180;
const MIN_LIST_HEIGHT = 200;

const KitRow = ({ style, index, collection, project, selectedIds, setSelected, handleCheck }) => (
  <KitListItem
    asset={collection[index]}
    style={style}
    project={project}
    setSelected={setSelected}
    checked={selectedIds.includes(collection[index]?.id)}
    onCheck={handleCheck}
  />
);

export default function SearchKits({
  project,
  data,
  loading,
  onLoadMore,
  search,
  setSearch,
  limit,
  setSelected,
  refetch,
  fetchTotalIds,
  headerActions,
}) {
  const { collection = [], metadata } = data?.assetsCollection || {};
  const totalCount = metadata?.totalCount || 0;
  const scroll = { entities: collection, totalCount, rowsCount: limit, onLoadMore, loading };
  const { rowCount, loadMoreRows, isRowLoaded } = useInfinityScroll(scroll);
  const onRowsRendered = useInfiniteLoader({ isRowLoaded, loadMoreRows, rowCount });
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRemoveAssets, { loading: loadingMutation }] = useMutation(ASSETS_BULK_REMOVE_MUTATION);
  // `totalCount` rather than a preloaded id list: the collection query no longer
  // ships every matching id, so select-all fetches them on click instead.
  const allSelected = totalCount > 0 && selectedIds.length === totalCount;
  const someSelected = selectedIds.length > 0 && !allSelected;
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const listContainerRef = useRef(null);
  const [listHeight, setListHeight] = useState(MIN_LIST_HEIGHT);
  const observerRef = useRef(null);

  const measureHeight = useCallback(() => {
    if (listContainerRef.current) {
      const height = listContainerRef.current.clientHeight;
      if (height > 0) setListHeight(Math.max(height, MIN_LIST_HEIGHT));
    }
  }, []);

  const listContainerCallbackRef = useCallback(
    node => {
      listContainerRef.current = node;
      if (observerRef.current) observerRef.current.disconnect();
      if (node) {
        observerRef.current = new ResizeObserver(measureHeight);
        observerRef.current.observe(node);
        measureHeight();
      }
    },
    [measureHeight]
  );

  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  const handleCheck = asset => {
    setSelectedIds(prev =>
      prev.includes(asset.id) ? prev.filter(id => id !== asset.id) : [...prev, asset.id]
    );
  };

  const handleRemoveSelected = () => {
    setShowRemoveModal(true);
  };

  const handleCloseRemoveModal = () => {
    setShowRemoveModal(false);
  };

  const handleConfirmRemove = async () => {
    if (selectedIds.length === 0) return;
    await bulkRemoveAssets({
      variables: { ids: selectedIds },
      onSuccess: () => setSelectedIds([]),
    });
    await refetch();
    setShowRemoveModal(false);
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
      return;
    }

    fetchTotalIds()
      .then(ids => setSelectedIds(ids || []))
      .catch(() => setSelectedIds([]));
  };

  // Keep the virtualized list mounted during fetchMore so react-window preserves
  // scroll position. The stub rows from useInfinityScroll already show loading-more state.
  const initialLoading = loading && collection.length === 0;

  return (
    <AssetsWrap
      height={undefined}
      loading={initialLoading}
      search={search}
      setSearch={setSearch}
      sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
      hideFilterChips
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" flexShrink={0}>
        <Box display="flex" alignItems="center">
          <FormControlLabel
            sx={{ ml: "8px" }}
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={handleSelectAll}
              />
            }
            label="Select All"
          />
        </Box>
        {selectedIds.length > 0 ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleRemoveSelected}
            disabled={loadingMutation}
          >
            Remove Kits ({selectedIds.length})
          </Button>
        ) : (
          headerActions || null
        )}
      </Box>
      <Box ref={listContainerCallbackRef} flex={1} minHeight={0}>
        <List
          style={{ height: listHeight, width: "100%" }}
          onRowsRendered={onRowsRendered}
          rowCount={rowCount}
          rowHeight={ITEM_HEIGHT}
          rowProps={{ collection, project, selectedIds, setSelected, handleCheck }}
          rowComponent={KitRow}
        />
      </Box>
      {showRemoveModal && (
        <ConfirmDeleteModal
          title="Remove Kits"
          descr="Are you sure you want to remove the selected kits?"
          onClose={handleCloseRemoveModal}
          onDelete={handleConfirmRemove}
          applyLabel="Remove"
        />
      )}
    </AssetsWrap>
  );
}
